import * as React from 'react';
import { cva } from 'class-variance-authority';
import { cn } from '@ordering/utils/styling';
import { Body, bodyVariants } from '@ordering/components/ui/Typography';
import { Alert } from '@sumup-oss/icons';

const inputBase = cva('w-full rounded-[8px] border bg-white px-4 h-12 flex items-center focus:outline-none', {
  variants: {
    error: {
      true: 'border-red-500',
      false: 'border-input',
    },
  },
  defaultVariants: {
    error: false,
  },
});

export interface InputProps extends React.InputHTMLAttributes<HTMLInputElement> {
  label: string;
  error?: string; 
  required?: boolean;
  hideLabel?: boolean;
}

export const Input = React.forwardRef<HTMLInputElement, InputProps>(
  (
    {
      id,
      className,
      label,
      error,
      required,
      hideLabel = false,
      ...rest
    },
    ref
  ) => (
      <div role="group" aria-labelledby={`${id}-group`}>
        <label id={`${id}-label`} htmlFor={id} className={cn(hideLabel ? 'sr-only' : 'mb-1 block')}>
          <Body variant="s" weight="medium" as="p" className="text-black mb-1">
            {label}
            {required && <span className="text-red-500 text-sm" aria-hidden="true"> *</span>}
          </Body>
        </label>

        <input
          ref={ref}
          id={id}
          className={cn(
            inputBase({ error: !!error }),
            bodyVariants({ variant: "m", weight: "regular" }),
            className
          )}
          aria-invalid={!!error || undefined}
          {...rest}
        />

        {error && (
          <div id={`${id}-error`} className="flex items-start gap-2 mt-2 text-red-600">
            <Alert size="16" />
            <span className="text-sm leading-tight break-words">{error}</span>
          </div>
        )}
      </div>
    )
);

Input.displayName = 'Input';
